import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';
import { MessagingAccount } from '../types';

/**
 * Stanje naloga za poruke i probno slanje.
 *
 * Ovo se dira jednom — kad se nalog podesi ili kad nešto prestane da radi —
 * pa stoji u podešavanjima, a ne uz posao dana. Sam spisak poslatih poruka
 * ostaje na „Danas", jer se on gleda svakodnevno.
 */
export default function MessagingSetup() {
  const [account, setAccount] = useState<MessagingAccount | null>(null);
  const [phone, setPhone] = useState('');
  const [mail, setMail] = useState('');
  const [result, setResult] = useState<string | null>(null);

  // Provera naloga se hvata da ekran ostane živ, ali se IMENUJE. Bez ovoga je pad
  // provere sklanjao CELU karticu (`if (!account) return null`), pa operater na
  // podešavanjima nije video ni da slanje poruka postoji — a pad provere je baš
  // trenutak kad mu to treba.
  const [proveraNijeProsla, setProveraNijeProsla] = useState(false);

  const load = useCallback(async () => {
    try {
      setAccount(await api.messagingAccount());
      setProveraNijeProsla(false);
    } catch {
      setAccount(null);
      setProveraNijeProsla(true);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Proba ide na broj koji operater sam ukuca — svoj, ne kupčev. Jedna
   * poruka košta koliko i jedna poruka; slanje naslepo pravom kupcu ne košta
   * ništa, ali se ne može povući.
   */
  const send = async (channel: 'viber' | 'sms' | 'mail') => {
    setResult(null);
    const kome = (channel === 'mail' ? mail : phone).trim();

    try {
      const sent = await api.testMessage(kome, channel);

      if (!sent.destination) {
        setResult(channel === 'mail'
          ? 'Adresa se ne da pročitati — proveri kako je ukucana.'
          : 'Broj se ne da pročitati — proveri kako je ukucan.');
        return;
      }

      // Mejl se šalje odmah, pa se ishod vidi na licu mesta: ako lozinka ne
      // valja, čovek to mora saznati sad, a ne tražeći poruku na „Danas".
      setResult(sent.status === 'Rejected'
        ? `Nije prošlo za ${sent.destination}: ${sent.greska ?? 'nepoznat razlog'}`
        : sent.status === 'Sent'
          ? `Poslato na ${sent.destination}.`
          : `Upisano u red za ${sent.destination} — stiže za koji minut, a ishod se vidi na „Sa terena" → Poruke.`);
    } catch (e) {
      setResult(e instanceof Error ? e.message : String(e));
    }
  };

  if (!account) {
    return proveraNijeProsla
      ? (
        <section className="panel">
          <h2>Slanje poruka</h2>
          <div className="health health--bad">
            Provera naloga za poruke nije prošla. To <strong>ne znači</strong> da
            slanje ne radi — znači da se stanje nije moglo pročitati. Osveži
            stranicu; ako se ponavlja, proveri je li Akton dostupan.
          </div>
        </section>
      )
      : null;
  }

  return (
    <section className="panel">
      <h2>Slanje poruka</h2>

      <p className="note">
        Vozaču ide Viber sa linkom ture čim je sačuvaš, poslovođi šta mu stiže
        i šta da spremi, a kupcu SMS kad vozač krene. Mejlom idu nalozi koji
        stoje: prenos u 012, pošalji u magacin, i podsetnik da se proknjiži
        prijem. Ovde se samo proverava da li kanal radi.
      </p>

      <ul className="setup__state">
        <li>
          <span>Viber</span>
          {account.viberReady
            ? <span className="flag flag--ok">podešen</span>
            : <span className="flag flag--bad">nije podešen</span>}
          <span className="muted">{account.sender || 'bez maske'}</span>
        </li>
        <li>
          <span>SMS</span>
          {!account.smsReady
            ? <span className="flag flag--bad">nije podešen</span>
            : account.smsAccountWorks
              ? <span className="flag flag--ok">nalog radi</span>
              : <span className="flag flag--bad">nalog nema pristup</span>}
          <span className="muted">
            {account.smsOriginator || 'bez maske'}
            {account.credit !== null && ` · kredit ${account.credit.toFixed(2)}`}
          </span>
        </li>
        <li>
          <span>Mejl</span>
          {account.mailReady
            ? <span className="flag flag--ok">podešen</span>
            : <span className="flag flag--bad">nije podešen</span>}
          {/* Kad fali, piše ŠTA fali. Tiho „nije podešeno" je najgori ishod:
              poruka stoji u redu bez greške, a čovek traži kvar u lozinci. */}
          <span className="muted">
            {account.mailReady ? 'nalozi objektima' : `fali: ${account.mailMissing}`}
          </span>
        </li>
      </ul>

      {/* Ime naloga i DUŽINA lozinke, nikad lozinka — po dužini se vidi ono
          što se inače ne vidi: lozinka koju je XML pojeo na znaku & ili <. */}
      <p className="muted">
        nalog: {account.username || '(prazno)'} · lozinka:{' '}
        {account.passwordLength > 0
          ? `${account.passwordLength} znakova`
          : <strong>PRAZNA</strong>}
      </p>

      {/*
        Razmak zalepljen sa lozinkom je najčešća greška pri kopiranju iz
        pregledača, i jedina koju sama dužina ne otkriva — ako se dužina ne
        prebroji tačno. Dodato 24.09.2026, kad je posle promene lozinke Akton i
        dalje vraćao 401 a dužina je „izgledala dobro".
      */}
      {account.passwordTrimmedLength !== account.passwordLength && (
        <div className="health health--bad">
          <strong>Lozinka ima razmak na kraju ili početku.</strong> U web.config
          stoji {account.passwordLength} znakova, a bez razmaka ih je{' '}
          {account.passwordTrimmedLength}. Akton tu lozinku odbija.
        </div>
      )}

      {account.usernameTrimmedLength !== (account.username?.length ?? 0) && (
        <div className="health health--bad">
          <strong>Ime naloga ima razmak na kraju ili početku.</strong>
        </div>
      )}

      {account.passwordHasXmlSpecials && (
        <div className="health health--unknown">
          Lozinka sadrži znak koji XML traži pisan drugačije (<code>&amp;</code>,{' '}
          <code>&lt;</code>, <code>&gt;</code>, <code>"</code> ili apostrof). U
          web.config mora stajati kao <code>&amp;amp;</code> odnosno{' '}
          <code>&amp;quot;</code> — inače vrednost nije ona koju si uneo.
        </div>
      )}

      <div className="setup__row">
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="svoj broj za probu, npr. 064/123-4567"
          aria-label="Broj za probnu poruku"
        />
        <button type="button" disabled={phone.trim() === ''} onClick={() => void send('viber')}>
          Proba Viber
        </button>
        <button type="button" disabled={phone.trim() === ''} onClick={() => void send('sms')}>
          Proba SMS
        </button>
      </div>

      <p className="note">Proba ide na broj koji ovde ukucaš — svoj, ne kupčev.</p>

      {/* Mejl je svoj kanal i svoje podešavanje (app password), pa ima i svoju
          probu: Viber koji radi ne govori ništa o tome da li mejl prolazi. */}
      <div className="setup__row">
        <input
          type="email"
          value={mail}
          onChange={(e) => setMail(e.target.value)}
          placeholder="svoja adresa za probu mejla"
          aria-label="Adresa za probni mejl"
        />
        <button type="button" disabled={mail.trim() === ''} onClick={() => void send('mail')}>
          Proba mejla
        </button>
      </div>

      <p className="note">
        Mejlom idu nalozi objektima — prenos u magacin 012 i „pošalji robu u
        magacin". Ishod probe se vidi odmah, jer se šalje na licu mesta.
      </p>

      {result && <p className="explanation">{result}</p>}
    </section>
  );
}
